import axios from "axios";
import constants from "../pages/cart/constants.json";

const regexValidations: { [key: string]: RegExp } = {
  text: /^[a-zA-Z]{3,}(\s[a-zA-Z]{1,})*$/,
  numtext: /^[a-zA-Z0-9.,-/']{1,30}(?:\s[a-zA-Z0-9.,-/']{1,30})*$/,
  telephone: /^\d{10}$/,
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,}){0,2}$/,
  postcode: /^\d{5}$/,
  company: /^[a-zA-Z0-9\s&.,\-']{2,100}$/,
};

export const fieldValidations = (
  value: string,
  isRequired: boolean,
  type: string
): string => {
  if (isRequired && value === "") {
    return "This field is required";
  }
  if (type && value !== "") {
    const regex = new RegExp(regexValidations[type]);
    if (!regex.test(value)) {
      return "Invalid input";
    }
  }
  return "";
};

type ErrorState = {
  adminEmailError: string | null;
  userEmailErrors: (string | null)[];
};

export const checkForDuplicateEmails = (
  adminEmail: string,
  userEmailArray: string[]
): ErrorState => {
  const errorState: ErrorState = {
    adminEmailError: "",
    userEmailErrors: Array(userEmailArray.length).fill(""),
  };

  const emailCount: { [key: string]: number } = {};

  // Count occurrences of each email and check for admin email in the same loop
  userEmailArray.forEach((email, index) => {
    emailCount[email] = (emailCount[email] || 0) + 1;
    if (email === adminEmail) {
      errorState.adminEmailError = constants.formSection.duplicateEmailError;
      errorState.userEmailErrors[index] =
        constants.formSection.duplicateEmailError;
    }
  });

  // Update error state for duplicates within userEmailArray
  userEmailArray.forEach((email, index) => {
    if (emailCount[email] > 1) {
      errorState.userEmailErrors[index] =
        constants.formSection.duplicateEmailError;
    }
  });

  return errorState;
};

const formatAddress = (
  addressLine1: string,
  addressLine2: string = "",
  city: string,
  state: string,
  country: string,
  zipcode: number
) => ({
  address: {
    rawAddress1: addressLine1,
    rawAddress2: addressLine2,
    rawAddress3: "",
    rawAddress4: "",
    rawAddress5: "",
    rawAddress6: "",
    rawCity: city,
    rawCountry: country,
    rawCounty: "",
    rawState: state,
    rawPostalCode: zipcode,
  },
});

const validatorFunction = async (apiPayload: any, apiURL: string) => {
  const axiosConfig = {
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      "Access-Control-Allow-Origin": "*",
    },
  };

  try {
    const res = await axios.post(apiURL, apiPayload, axiosConfig);
    return res.data;
  } catch (err) {
    return { success: false, statusCode: "500", data: null };
  }
};

export const validateEmail = async (
  adminEmail: string,
  storeCode: string,
  apiBaseURL: string
) => {
  const apiPayload = { EmailAddress: adminEmail, StoreKey: storeCode };
  const apiURL = `${apiBaseURL}${constants.emailValidation.apiURL}`;
  const result = await validatorFunction(apiPayload, apiURL);

  const { success: isAPISuccess, statusCode: apiStatusCode, data } = result;
  const { isStoreValid, isEmailValid, isDomainBlocked } = data || {};

  const isValidationSuccess =
    (isAPISuccess &&
      apiStatusCode === "200" &&
      isStoreValid === true &&
      isEmailValid === true &&
      isDomainBlocked === false) ||
    apiStatusCode !== "200";

  if (!isValidationSuccess) {
    return constants.emailValidation.message;
  }
  return "";
};

export const validateAddress = async (
  address1: string,
  address2: string,
  city: string,
  state: string,
  country: string,
  postCode: number,
  apiBaseURL: string
) => {
  const apiPayload = formatAddress(
    address1,
    address2,
    city,
    state,
    country,
    postCode
  );

  const apiURL = `${apiBaseURL}${constants.addressValidation.apiURL}`;
  const result = await validatorFunction(apiPayload, apiURL);

  const { success: isAPISuccess, statusCode: apiStatusCode, data } = result;
  const { isAddressValid, isAddressCorrected, address } = data || {};

  const isValidationSuccess =
    (isAPISuccess && apiStatusCode === "200" && isAddressValid === "Y") ||
    apiStatusCode !== "200";

  if (!isValidationSuccess) {
    return { message: constants.addressValidation.invalidAddress.message };
  } else {
    if (isAddressCorrected === "Y") {
      return {
        message: constants.addressValidation.formatting.message,
        correctedAddress: address,
      };
    }
  }
  return { message: "" };
};
